import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Loader } from '../../components/Loader'
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { fetchFoundStockDeclaration } from '../../services/foundStockServices'
import { retryUnlessUnauthorized } from './foundStockErrors'
import { foundStockQueryKeys } from './foundStockQueryKeys'
import { useProductSummaries } from './useProductSummaries'
import { ProductCell } from './ProductCell'
import { LotCell } from './LotCell'

const T = 'found_stock.detail'
const BUCKETS = ['found', 'retired', 'corrected_out', 'converted', 'recovered']

// Fixed order and always five rows: an empty bucket is information too.
export const bucketRows = units => BUCKETS.map(key => ({ key, ids: (units && units[key]) || [] }))

const Fact = ({ label, children, testId }) => (
  <div className="py-1">
    <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-800" data-testid={testId}>
      {children}
    </dd>
  </div>
)

const change = (before, after) => `${before} → ${after}`

export const FoundStockDetailPanel = ({ warehouseId, declarationId }) => {
  const { i18n } = useTranslation()
  const [openBuckets, setOpenBuckets] = useState({})
  const { data, isLoading, isError } = useQuery(
    foundStockQueryKeys.declaration(warehouseId, declarationId),
    () => fetchFoundStockDeclaration(warehouseId, declarationId),
    { refetchOnWindowFocus: false, enabled: declarationId != null, retry: retryUnlessUnauthorized },
  )
  const declaration = data?.declaration
  const productIds = useMemo(() => (declaration ? [declaration.product_id] : []), [declaration])
  const summaries = useProductSummaries(productIds)

  if (isLoading) return <Loader show />
  if (isError || !declaration) return <p className="text-sm text-red-600">{i18n.t(`${T}.load_error`)}</p>

  const bayLabel = locationId =>
    locationId === declaration.warehouse_location_id ? declaration.location_code : i18n.t(`${T}.location_fallback`, { id: locationId })
  const userLabel = (id, name) => name || i18n.t(`${T}.user_fallback`, { id })
  const buckets = bucketRows(data.units)

  return (
    <div className="text-gray-700">
      <dl className="grid grid-cols-2 gap-x-4">
        <Fact label={i18n.t(`${T}.declaration`)}>#{declaration.id}</Fact>
        <Fact label={i18n.t(`${T}.source`)}>
          {i18n.t(`found_stock.source.${declaration.source}`, { defaultValue: declaration.source })}
          {declaration.cycle_count_id != null && ` · ${i18n.t('found_stock.queue.cycle_count', { id: declaration.cycle_count_id })}`}
        </Fact>
        <Fact label={i18n.t(`${T}.product`)}>
          <ProductCell productId={declaration.product_id} summary={summaries[declaration.product_id]} />
        </Fact>
        <Fact label={i18n.t(`${T}.lot`)}>
          <LotCell row={declaration} />
        </Fact>
        <Fact label={i18n.t(`${T}.location`)}>{declaration.location_code}</Fact>
        <Fact label={i18n.t(`${T}.declared_by`)}>
          {userLabel(declaration.declared_by_id, declaration.declared_by_name)}
          <span className="text-gray-400 ml-1">
            <FormattedRelativeTime date={declaration.declared_at} />
          </span>
        </Fact>
        <Fact label={i18n.t(`${T}.declared_quantity`)} testId="declared-quantity">
          {declaration.quantity}
        </Fact>
        <Fact label={i18n.t(`${T}.effective_quantity`)} testId="effective-quantity">
          {declaration.effective_quantity}
        </Fact>
        <Fact label={i18n.t(`${T}.live_quantity`)} testId="live-quantity">
          {(data.units?.found || []).length}
        </Fact>
      </dl>

      <h3 className="text-sm font-semibold mt-5 mb-2">{i18n.t(`${T}.units_title`)}</h3>
      <ul data-testid="found-stock-buckets" className="divide-y">
        {buckets.map(b => (
          <li key={b.key} className="py-2 flex items-start justify-between">
            <div>
              <span data-testid="bucket-label" className="text-sm">
                {i18n.t(`found_stock.buckets.${b.key}`)}
              </span>
              {openBuckets[b.key] && b.ids.length > 0 && <div className="text-xs text-gray-500 mt-1">{b.ids.join(', ')}</div>}
            </div>
            {/* Fixed-width count and an always-present (if invisible) toggle: the five rows then
                read as a column of numbers instead of five differently indented ones. */}
            <div className="flex items-center gap-3">
              <span data-testid="bucket-count" className="w-8 text-right tabular-nums text-sm font-semibold">
                {b.ids.length}
              </span>
              <button
                type="button"
                className={`text-xs text-indigo-600 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded ${
                  b.ids.length === 0 ? 'invisible' : ''
                }`}
                disabled={b.ids.length === 0}
                onClick={() => setOpenBuckets(s => ({ ...s, [b.key]: !s[b.key] }))}
              >
                {i18n.t(`${T}.${openBuckets[b.key] ? 'hide_ids' : 'show_ids'}`)}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <h3 className="text-sm font-semibold mt-5 mb-2">{i18n.t(`${T}.corrections_title`)}</h3>
      {(data.corrections || []).length === 0 ? (
        <p className="text-sm text-gray-500">{i18n.t(`${T}.corrections_empty`)}</p>
      ) : (
        <table className="min-w-full text-sm" data-testid="found-stock-corrections">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-2">{i18n.t(`${T}.correction_when`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.correction_change`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.correction_by`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.correction_units`)}</th>
            </tr>
          </thead>
          <tbody>
            {data.corrections.map(c => (
              <tr key={c.id} className="border-t">
                <td className="py-1 pr-2">
                  <FormattedRelativeTime date={c.recorded_at} />
                </td>
                <td className="py-1 px-2">{change(c.quantity_before, c.quantity_after)}</td>
                <td className="py-1 px-2">{userLabel(c.created_by_id)}</td>
                <td className="py-1 px-2 text-xs text-gray-500">
                  {(c.added_stock_unit_ids || []).length > 0 && <div>{i18n.t(`${T}.correction_added`, { count: c.added_stock_unit_ids.length })}</div>}
                  {(c.retired_stock_unit_ids || []).length > 0 && <div>{i18n.t(`${T}.correction_retired`, { count: c.retired_stock_unit_ids.length })}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="text-sm font-semibold mt-5 mb-2">{i18n.t(`${T}.legs_title`)}</h3>
      {(data.legs || []).length === 0 ? (
        <p className="text-sm text-gray-500">{i18n.t(`${T}.legs_empty`)}</p>
      ) : (
        <table className="min-w-full text-sm" data-testid="found-stock-legs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 pr-2">{i18n.t(`${T}.leg_when`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.leg_action`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.leg_location`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.leg_change`)}</th>
              <th className="py-1 px-2">{i18n.t(`${T}.leg_by`)}</th>
            </tr>
          </thead>
          <tbody>
            {data.legs.map(l => (
              <tr key={l.id} className="border-t">
                <td className="py-1 pr-2">
                  <FormattedRelativeTime date={l.executed_at} />
                </td>
                <td className="py-1 px-2">{l.action}</td>
                <td className="py-1 px-2">{bayLabel(l.warehouse_location_id)}</td>
                <td className="py-1 px-2">{change(l.previous_stock_quantity, l.new_stock_quantity)}</td>
                <td className="py-1 px-2">{userLabel(l.created_by_id)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
